import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { solicitarReset, actualizarContrasenia } from "../api/usuarios";
import { getRestauranteActual } from "../api/public";
import { getImageUrl } from "../utils/imageUrl";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";
import fondo from "../assets/fondo-restaurante.jpg";

const RecuperarContrasenia = () => {
  const [step, setStep] = useState(1);
  const [correo, setCorreo] = useState("");
  const [token, setToken] = useState("");
  const [nueva, setNueva] = useState("");
  const [repetir, setRepetir] = useState("");
  const [mostrarNueva, setMostrarNueva] = useState(false);
  const [mostrarRepetir, setMostrarRepetir] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(false);
  const [logo, setLogo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const cargarLogo = async () => {
      try {
        const restaurante = await getRestauranteActual();
        if (restaurante?.logo) {
          const logoUrl = `${getImageUrl(restaurante.logo)}?t=${Date.now()}`;
          setLogo(logoUrl);
        }
      } catch (err) {
        console.error('Error cargando logo en RecuperarContrasenia:', err);
      }
    };

    cargarLogo();
  }, []);

  const handleSolicitar = async (e) => {
    e.preventDefault();
    setError("");
    setMensaje("");
    setCargando(true);
    
    try {
      const res = await solicitarReset(correo);
      setMensaje(res.message);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || "Error al solicitar recuperación");
    } finally {
      setCargando(false);
    }
  };

  const handleActualizar = async (e) => {
    e.preventDefault();
    setError("");
    setMensaje("");

    if (nueva !== repetir) {
      setError("Las contraseñas no coinciden");
      return;
    }

    setCargando(true);
    try {
      const res = await actualizarContrasenia({
        correo,
        token,
        nuevaContrasenia: nueva,
        repetirContrasenia: repetir,
      });
      setMensaje(res.message);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || "Error al actualizar contraseña");
    } finally {
      setCargando(false);
    }
  };

  // Redirige al login cuando step=3
  useEffect(() => {
    if (step === 3) {
      const timer = setTimeout(() => {
        navigate("/login");
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [step, navigate]);

  return (
    <div
      className="flex items-center justify-center min-h-screen bg-cover bg-center"
      style={{ backgroundImage: `url(${fondo})` }}
    >
      <div className="bg-amber-950/60 p-8 rounded-3xl shadow-2xl w-96 max-w-full backdrop-blur-md border border-amber-900/30">
        {/* Logo del restaurante */}
        <div className="flex justify-center mb-6">
          {logo && <img src={logo} alt="Logo Restaurante" className="h-36 w-auto object-contain drop-shadow-md" />}
        </div>

        {step === 1 && (
          <>
            <h2 className="text-2xl font-bold mb-6 text-center text-white drop-shadow-md">
              Recuperar Contraseña
            </h2>
            <form onSubmit={handleSolicitar} className="space-y-4">
              <input
                type="email"
                placeholder="Correo electrónico"
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition bg-white/95 text-gray-900"
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
                required
              />
              <button
                type="submit"
                disabled={cargando}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-semibold disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg"
              >
                {cargando ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  "Enviar código"
                )}
              </button>
            </form>
            <p
              className="text-sm text-center text-amber-300 hover:text-amber-200 font-semibold underline underline-offset-4 mt-5 cursor-pointer transition drop-shadow-sm"
              onClick={() => navigate("/login")}
            >
              Volver a iniciar sesión
            </p>
          </>
        )}

        {step === 2 && (
          <>
            <h2 className="text-2xl font-bold mb-6 text-center text-white drop-shadow-md">
              Restablecer Contraseña
            </h2>
            <form onSubmit={handleActualizar} className="space-y-4">
              <input
                type="text"
                placeholder="Clave temporal"
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition bg-white/95 text-gray-900"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                required
              />

              {/* Nueva contraseña */}
              <div className="relative">
                <input
                  type={mostrarNueva ? "text" : "password"}
                  placeholder="Nueva contraseña"
                  className="w-full p-3 pr-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition bg-white/95 text-gray-900"
                  value={nueva}
                  onChange={(e) => setNueva(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setMostrarNueva(!mostrarNueva)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-amber-800 focus:outline-none p-1 transition"
                  title={mostrarNueva ? "Ocultar contraseña" : "Ver contraseña"}
                  tabIndex={-1}
                >
                  {mostrarNueva ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {/* Repetir contraseña */}
              <div className="relative">
                <input
                  type={mostrarRepetir ? "text" : "password"}
                  placeholder="Repetir contraseña"
                  className="w-full p-3 pr-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition bg-white/95 text-gray-900"
                  value={repetir}
                  onChange={(e) => setRepetir(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setMostrarRepetir(!mostrarRepetir)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-amber-800 focus:outline-none p-1 transition"
                  title={mostrarRepetir ? "Ocultar contraseña" : "Ver contraseña"}
                  tabIndex={-1}
                >
                  {mostrarRepetir ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              <button
                type="submit"
                disabled={cargando}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-semibold disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg"
              >
                {cargando ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  "Actualizar"
                )}
              </button>
            </form>
            <p
              className="text-sm text-center text-amber-300 hover:text-amber-200 font-semibold underline underline-offset-4 mt-5 cursor-pointer transition drop-shadow-sm"
              onClick={() => setStep(1)}
            >
              ← Solicitar otro código
            </p>
          </>
        )}

        {step === 3 && (
          <div className="text-center py-4">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-2" />
            <h2 className="text-2xl font-bold text-emerald-400 drop-shadow-md mb-2">
              {mensaje || "Contraseña actualizada exitosamente"}
            </h2>
            <p className="text-amber-100 text-sm">
              Redirigiendo al inicio de sesión...
            </p>
          </div>
        )}

        {mensaje && step !== 3 && (
          <p className="text-amber-100 text-sm mt-4 text-center bg-amber-900/60 p-3 rounded-lg border border-amber-600/40">
            {mensaje}
          </p>
        )}
        {error && (
          <p className="text-red-300 text-sm mt-4 text-center bg-red-950/60 p-3 rounded-lg border border-red-500/40 font-medium">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default RecuperarContrasenia;
